/**
 * KpiCard — reusable KPI metric card for the admin dashboard.
 *
 * data-testid: candeza-dashboard-kpi-{id}
 * Loading state: candeza-dashboard-kpi-{id}-loading
 */

import React from 'react';
import styled from 'styled-components';

export interface KpiCardProps {
  id: string;
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  error?: boolean;
  subtitle?: string;
}

const Wrapper = styled.div<{ $color: string }>`
  background: ${({ theme }) => theme.colors.cardBgGradient || theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  position: relative;
  overflow: hidden;
  transition: transform 0.15s, box-shadow 0.15s;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows?.lg || '0 8px 24px rgba(0,0,0,0.15)'};
  }
`;

const IconBox = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
`;

const Value = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl'] || '1.5rem'};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.2;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary || theme.colors.textSecondary};
  margin-top: 2px;
`;

const Skeleton = styled.div`
  height: 28px;
  width: 60%;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: 4px;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

export const KpiCard: React.FC<KpiCardProps> = ({
  id,
  label,
  value,
  icon,
  color,
  loading = false,
  error = false,
  subtitle,
}) => {
  const testId = `candeza-dashboard-kpi-${id}`;
  return (
    <Wrapper $color={color} data-testid={testId}>
      <IconBox $color={color}>{icon}</IconBox>
      <Content>
        {loading ? (
          <Skeleton data-testid={`${testId}-loading`} />
        ) : error ? (
          <Value style={{ fontSize: '0.9rem', color: '#ef4444' }} data-testid={`${testId}-error`}>
            —
          </Value>
        ) : (
          <Value>{value}</Value>
        )}
        <Label>{label}</Label>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Content>
    </Wrapper>
  );
};

export default KpiCard;

